/**
 * Signavio export detection & cleanup.
 *
 * Signavio Process Editor exports BPMN 2.0 with a Signavio-specific namespace
 * (`xmlns:signavio="http://www.signavio.com"`) that adds metadata, label
 * positions and revision IDs ("Historie") via `<signavio:*>` elements. These
 * are non-standard, blow up the file size and are useless for downstream
 * editors like bpmn-js.
 *
 * `detectSignavio` reports whether a given XML string looks like a Signavio
 * export. `cleanSignavio` removes all Signavio-specific noise without touching
 * the BPMN diagram structure (BPMNDI, sequenceFlow, ioSpecification stay
 * intact).
 *
 * No external dependency: uses the browser-native `DOMParser` /
 * `XMLSerializer`. In Node test environments (jsdom) these are available too.
 */

const SIGNAVIO_NS = 'http://www.signavio.com';
const I18N_NS = 'http://www.omg.org/spec/BPMN/non-normative/extensions/i18n/1.0';

/**
 * Detect whether `xml` is a Signavio export.
 *
 * @param {string} xml raw BPMN 2.0 XML
 * @returns {{
 *   isSignavio: boolean,
 *   signals: string[],
 *   counts: {
 *     signavioMetaData: number,
 *     signavioLabel: number,
 *     signavioDiagramMetaData: number,
 *     signavioElementsTotal: number,
 *     extensionElements: number,
 *   }
 * }}
 */
export function detectSignavio(xml) {
  const empty = {
    isSignavio: false,
    signals: [],
    counts: {
      signavioMetaData: 0,
      signavioLabel: 0,
      signavioDiagramMetaData: 0,
      signavioElementsTotal: 0,
      extensionElements: 0,
    },
  };

  if (typeof xml !== 'string' || xml.length === 0) return empty;

  const signals = [];
  if (xml.includes('xmlns:signavio="http://www.signavio.com"')) {
    signals.push('xmlns:signavio');
  }
  if (xml.includes('exporter="Signavio Process Editor"')) {
    signals.push('exporter=Signavio');
  }
  if (xml.includes('targetNamespace="http://www.signavio.com"')) {
    signals.push('targetNamespace=signavio.com');
  }
  if (xml.includes('<signavio:')) {
    signals.push('<signavio:*> elements');
  }

  if (signals.length === 0) return empty;

  // Cheap counts via regex — good enough for telemetry, no parsing required.
  const countMatches = (re) => (xml.match(re) || []).length;
  const counts = {
    signavioMetaData: countMatches(/<signavio:signavioMetaData\b/g),
    signavioLabel: countMatches(/<signavio:signavioLabel\b/g),
    signavioDiagramMetaData: countMatches(/<signavio:signavioDiagramMetaData\b/g),
    signavioElementsTotal: countMatches(/<signavio:[a-zA-Z]/g),
    extensionElements: countMatches(/<(?:[a-zA-Z][\w-]*:)?extensionElements\b/g),
    itSystemElements: countMatches(/<signavio:signavioType[^>]*dataObjectType="IT-System"/g),
  };

  return { isSignavio: true, signals, counts };
}

/**
 * Remove all Signavio-specific noise from the XML.
 *
 * Removed:
 *  - All elements in the Signavio namespace (`<signavio:*>`)
 *  - Empty `<extensionElements/>` wrappers (also when prefixed)
 *  - `signavio:*` attributes on all BPMN elements (e.g. `signavio:alignment` on `textAnnotation`)
 *  - IT-System `textAnnotation` elements: the attribute `custom:itSystem="true"`
 *    is added so the custom renderer draws the Signavio computer icon shape.
 *    The `<text>` label (system name) is kept unchanged.
 *  - Branding attributes `exporter` / `exporterVersion` on the root
 *  - `xmlns:signavio` and `xmlns:i18n` namespace declarations on the root
 *    (only if no remaining elements/attributes use them — they should always
 *    be unused after the Signavio elements are gone)
 *  - `targetNamespace="http://www.signavio.com"` is replaced with the
 *    bpmn.io default namespace
 *  - `custom:*` attributes on the root (`custom:version`, `custom:author`,
 *    `custom:lastModified`, `custom:versionHistory`, `custom:imageStore`),
 *    so the file starts with a fresh editor state. The first save after a
 *    Signavio import should produce a slim file, not a 1.3 MB snapshot of
 *    itself.
 *
 * Untouched:
 *  - All `<bpmndi:*>` shape/edge layout
 *  - `<sequenceFlow>`, `<incoming>`, `<outgoing>` and any other standard
 *    BPMN 2.0 structure
 *  - `<dataStore>` definitions (orphan check is reported as a warning only)
 *  - All element IDs (sid-... is left in place — the modeler will keep
 *    diagram references valid)
 *
 * @param {string} xml raw BPMN 2.0 XML
 * @returns {{
 *   cleanedXml: string,
 *   removed: {
 *     signavioElements: number,
 *     emptyExtensionElements: number,
 *     attributes: number,
 *     namespaces: number,
 *     targetNamespaceRewritten: boolean,
 *     itSystemConversions: number,
 *   },
 *   warnings: string[],
 * }}
 */
export function cleanSignavio(xml) {
  const removed = {
    signavioElements: 0,
    emptyExtensionElements: 0,
    attributes: 0,
    namespaces: 0,
    targetNamespaceRewritten: false,
    itSystemConversions: 0,
  };
  const warnings = [];

  if (typeof xml !== 'string' || xml.length === 0) {
    return { cleanedXml: xml, removed, warnings };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  // Surface XML parse errors instead of producing garbage.
  const parseError = doc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    warnings.push('XML parse error — returning original XML unchanged.');
    return { cleanedXml: xml, removed, warnings };
  }

  // 0. Identify IT-System textAnnotations before Signavio children are removed.
  // Signavio represents external IT systems (computer icon) as <textAnnotation>
  // elements with <signavio:signavioType dataObjectType="IT-System"/> — which is
  // non-standard BPMN. bpmn-js cannot render the computer shape, so we prefix
  // the text with "[IT-System]" to keep the information visible.
  const allTextAnnotations = Array.from(doc.getElementsByTagName('*')).filter(
    (el) => el.localName === 'textAnnotation',
  );
  const itSystemIds = new Set();
  for (const ta of allTextAnnotations) {
    const signavioTypes = Array.from(ta.getElementsByTagNameNS(SIGNAVIO_NS, 'signavioType'));
    if (signavioTypes.some((el) => el.getAttribute('dataObjectType') === 'IT-System')) {
      itSystemIds.add(ta.getAttribute('id'));
    }
  }

  // 1. Remove every element in the Signavio namespace.
  // getElementsByTagNameNS returns a live HTMLCollection — copy to array first.
  const signavioEls = Array.from(doc.getElementsByTagNameNS(SIGNAVIO_NS, '*'));
  for (const el of signavioEls) {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
      removed.signavioElements += 1;
    }
  }

  // 2. Remove empty <extensionElements/> wrappers (any namespace).
  // After step 1, the Signavio-only ones are empty.
  const extEls = Array.from(doc.getElementsByTagName('*')).filter(
    (el) => el.localName === 'extensionElements',
  );
  for (const el of extEls) {
    // "Empty" means: no element children and no non-whitespace text.
    const hasChildElements = el.children && el.children.length > 0;
    const text = (el.textContent || '').trim();
    if (!hasChildElements && text.length === 0 && el.parentNode) {
      el.parentNode.removeChild(el);
      removed.emptyExtensionElements += 1;
    }
  }

  // 2.5 Strip signavio:* attributes from all BPMN elements (e.g. signavio:alignment
  // on <textAnnotation>). Namespace declarations (xmlns:*) are handled in step 3.
  for (const el of Array.from(doc.getElementsByTagName('*'))) {
    const sigAttrNames = Array.from(el.attributes)
      .map((a) => a.name)
      .filter((name) => name.startsWith('signavio:'));
    for (const name of sigAttrNames) {
      el.removeAttribute(name);
      removed.attributes += 1;
    }
  }

  // 2.6 Convert IT-System textAnnotations: add custom:itSystem="true" so the
  // custom renderer draws the computer icon. The text label is kept as-is.
  for (const ta of allTextAnnotations) {
    if (!itSystemIds.has(ta.getAttribute('id'))) continue;
    ta.setAttribute('custom:itSystem', 'true');
    removed.itSystemConversions += 1;
  }

  // 3. Touch the document element: remove branding + Signavio namespace decls,
  // rewrite targetNamespace.
  const root = doc.documentElement;
  if (root) {
    // Ensure xmlns:custom is declared when we added custom:itSystem attributes.
    if (removed.itSystemConversions > 0 && !root.hasAttribute('xmlns:custom')) {
      root.setAttribute('xmlns:custom', 'http://custom/schema/1.0');
    }
    if (root.hasAttribute('exporter')) {
      root.removeAttribute('exporter');
      removed.attributes += 1;
    }
    if (root.hasAttribute('exporterVersion')) {
      root.removeAttribute('exporterVersion');
      removed.attributes += 1;
    }
    if (root.getAttribute('targetNamespace') === SIGNAVIO_NS) {
      root.setAttribute('targetNamespace', 'http://bpmn.io/schema/bpmn');
      removed.targetNamespaceRewritten = true;
    }
    // xmlns:* attributes are exposed as regular attributes.
    if (root.hasAttribute('xmlns:signavio')) {
      root.removeAttribute('xmlns:signavio');
      removed.namespaces += 1;
    }
    // Drop any pre-existing editor version-history bloat on the root.
    // A fresh Signavio export should never carry custom:* attributes, but
    // re-imports of files that round-tripped through this editor can.
    const customAttrNames = Array.from(root.attributes)
      .map((a) => a.name)
      .filter((name) => name.startsWith('custom:'));
    for (const name of customAttrNames) {
      root.removeAttribute(name);
      removed.attributes += 1;
    }
    // Also drop the xmlns:custom declaration if no element still uses it.
    if (root.hasAttribute('xmlns:custom')) {
      const stillUsesCustom = Array.from(doc.getElementsByTagName('*')).some((el) => {
        if (el.prefix === 'custom') return true;
        for (const attr of Array.from(el.attributes)) {
          if (attr.prefix === 'custom' || attr.name.startsWith('custom:')) return true;
        }
        return false;
      });
      if (!stillUsesCustom) {
        root.removeAttribute('xmlns:custom');
        removed.namespaces += 1;
      }
    }
    if (root.hasAttribute('xmlns:i18n') && root.getAttribute('xmlns:i18n') === I18N_NS) {
      // i18n is also Signavio-only in practice — drop it if no element/attr
      // references the prefix anymore.
      const stillReferenced = Array.from(doc.getElementsByTagName('*')).some((el) => {
        if (el.prefix === 'i18n') return true;
        for (const attr of Array.from(el.attributes)) {
          if (attr.prefix === 'i18n' || attr.name.startsWith('i18n:')) return true;
        }
        return false;
      });
      if (!stillReferenced) {
        root.removeAttribute('xmlns:i18n');
        removed.namespaces += 1;
      }
    }
  }

  // 4. Orphan dataStore warning (informational only — never auto-removed).
  const dataStores = Array.from(doc.getElementsByTagName('*')).filter(
    (el) => el.localName === 'dataStore' && el.parentNode === root,
  );
  if (dataStores.length > 0) {
    const ids = dataStores.map((d) => d.getAttribute('id')).filter(Boolean);
    const refRe = new RegExp(
      `(?:dataStoreRef|sourceRef|targetRef)="(?:${ids.map((id) => id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})"`,
    );
    if (ids.length > 0 && !refRe.test(xml)) {
      warnings.push(
        `${dataStores.length} <dataStore> element(s) on root appear to be unreferenced. ` +
          'They were kept — remove manually if not needed.',
      );
    }
  }

  const serializer = new XMLSerializer();
  let cleanedXml = serializer.serializeToString(doc);

  // Re-add XML declaration if the parser dropped it.
  if (!cleanedXml.startsWith('<?xml')) {
    cleanedXml = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleanedXml;
  }

  return { cleanedXml, removed, warnings };
}

/**
 * Convenience: detect & clean in one call. Returns the original XML untouched
 * when the file is not a Signavio export.
 *
 * @param {string} xml
 * @returns {{
 *   xml: string,
 *   wasSignavio: boolean,
 *   detection: ReturnType<typeof detectSignavio>,
 *   cleanup: ReturnType<typeof cleanSignavio> | null,
 * }}
 */
export function detectAndClean(xml) {
  const detection = detectSignavio(xml);
  if (!detection.isSignavio) {
    return { xml, wasSignavio: false, detection, cleanup: null };
  }
  const cleanup = cleanSignavio(xml);
  return { xml: cleanup.cleanedXml, wasSignavio: true, detection, cleanup };
}
